import React, { useState } from "react";
import { ShieldCheck, ShieldOff, Loader, Check, AlertTriangle } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";

/**
 * TwoFactorSetup — admin self-service to enable / disable TOTP 2FA.
 * Three states:
 *   - enabled === true   → show "Disable" form
 *   - phase === "init"   → show "Enable" CTA
 *   - phase === "setup"  → show QR + 6-digit confirm
 *   - phase === "codes"  → show recovery codes (one-time)
 */
export default function TwoFactorSetup({ enabled, onChange }) {
  const [phase, setPhase] = useState(enabled ? "on" : "init");
  const [qr, setQr] = useState(null);
  const [secret, setSecret] = useState("");
  const [code, setCode] = useState("");
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [recovery, setRecovery] = useState([]);

  const startSetup = async () => {
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/setup-init");
      setQr(data.qr_data_url);
      setSecret(data.secret);
      setPhase("setup");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const confirmSetup = async () => {
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/setup-confirm", { code });
      setRecovery(data.recovery_codes || []);
      setPhase("codes");
      onChange?.(true);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const disable = async () => {
    setErr(""); setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { password: pwd });
      setPhase("init"); setPwd("");
      onChange?.(false);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="pt-4" style={{ borderTop: "3px dashed #20C1A7" }} data-testid="totp-setup">
      <div className="rm-tag mb-2"
           style={{ background: "#20C1A7", borderColor: "#20C1A7", color: "white" }}>
        Security · Two-factor auth
      </div>
      <h3 className="font-display text-lg uppercase mb-2 flex items-center gap-2">
        {phase === "on" ? <ShieldCheck size={18} /> : <ShieldOff size={18} />}
        {phase === "on" ? "2FA is on" : "2FA is off"}
      </h3>

      {phase === "init" && (
        <>
          <p className="text-sm mb-3" style={{ color: "#475569" }}>
            Add an extra layer to your admin account. We'll show you a QR code to scan with
            Google Authenticator, Authy, or 1Password — then you'll need a 6-digit code on every login.
          </p>
          <button onClick={startSetup} disabled={busy}
                  className="rm-btn rm-btn-primary text-xs"
                  data-testid="totp-start-btn">
            {busy ? <><Loader size={12} className="animate-spin" /> Loading…</> : <><ShieldCheck size={12} /> Set up 2FA</>}
          </button>
        </>
      )}

      {phase === "setup" && (
        <div className="space-y-3" data-testid="totp-setup-card">
          <p className="text-sm" style={{ color: "#475569" }}>
            <b>1.</b> Scan this QR with your authenticator app, then enter the 6-digit code below to confirm.
          </p>
          {qr && <img src={qr} alt="2FA QR code" style={{ width: 180, height: 180, border: "3px solid #0F1B2D", padding: 6, background: "white" }} />}
          <details className="text-xs" style={{ color: "#64748B" }}>
            <summary className="cursor-pointer">Can't scan? Show secret</summary>
            <div className="mt-1 font-mono select-all p-2 rounded" style={{ background: "#F1F5F9", color: "#0F1B2D" }}>
              {secret}
            </div>
          </details>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            placeholder="6-digit code"
            className="w-full max-w-[180px] text-lg font-mono tracking-[0.4em] p-3 text-center"
            style={{ background: "white", border: "3px solid #0F1B2D", borderRadius: 6 }}
            data-testid="totp-confirm-code"
          />
          <div className="flex gap-2">
            <button onClick={confirmSetup} disabled={busy || code.length !== 6}
                    className="rm-btn rm-btn-primary text-xs disabled:opacity-50"
                    data-testid="totp-confirm-btn">
              {busy ? <><Loader size={12} className="animate-spin" /> Verifying…</> : <><Check size={12} /> Confirm &amp; enable</>}
            </button>
            <button onClick={() => setPhase("init")} className="rm-btn rm-btn-ghost text-xs">
              Cancel
            </button>
          </div>
        </div>
      )}

      {phase === "codes" && (
        <div className="space-y-3" data-testid="totp-recovery-codes">
          <p className="text-sm flex items-start gap-2" style={{ color: "#0F1B2D" }}>
            <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" style={{ color: "#FF6A00" }} />
            <span><b>Save these recovery codes now.</b> Each one is single-use and lets you log in if
            you lose your authenticator. They'll never be shown again.</span>
          </p>
          <div className="grid grid-cols-2 gap-2 font-mono text-xs p-3"
               style={{ background: "#FFFBEB", border: "3px solid #0F1B2D" }}>
            {recovery.map((c, i) => (
              <div key={i} className="select-all">{c}</div>
            ))}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => navigator.clipboard.writeText(recovery.join("\n"))}
              className="rm-btn rm-btn-ghost text-xs"
              data-testid="totp-copy-codes-btn"
            >Copy all</button>
            <button onClick={() => setPhase("on")} className="rm-btn rm-btn-primary text-xs">
              I've saved them
            </button>
          </div>
        </div>
      )}

      {phase === "on" && (
        <div className="space-y-3">
          <p className="text-sm" style={{ color: "#475569" }}>
            Two-factor is active — you'll need a 6-digit code on every login. To turn it off,
            confirm your password below.
          </p>
          <input
            type="password"
            value={pwd}
            onChange={(e) => setPwd(e.target.value)}
            placeholder="Your password"
            className="w-full max-w-xs p-2"
            style={{ background: "white", border: "3px solid #0F1B2D", borderRadius: 6 }}
            data-testid="totp-disable-pwd"
          />
          <button onClick={disable} disabled={busy || !pwd}
                  className="rm-btn rm-btn-ghost text-xs disabled:opacity-50"
                  data-testid="totp-disable-btn">
            {busy ? <><Loader size={12} className="animate-spin" /> Disabling…</> : <><ShieldOff size={12} /> Disable 2FA</>}
          </button>
        </div>
      )}

      {err && <p className="text-xs mt-2" style={{ color: "#991B1B" }} data-testid="totp-error">{err}</p>}
    </div>
  );
}
